import React, {Fragment, useContext, useEffect, useLayoutEffect, useState} from 'react';
import {StyleType} from 'patient-ping-remedy/packages/button';
import Icon from 'patient-ping-remedy/packages/icon';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {
  StyledButton,
  StyledControlsContainer,
  StyledDisclaimerText,
  StyledDropdownContainer,
  StyledIconButton,
  StyledIconClose,
  StyledIconWrapper,
  StyledIdDropdownList,
  StyledIdInput,
  StyledLabelFormFieldTypography,
  StyledOtherNameInput,
  StyledRadioButton,
  StyledRow,
  StyledSmallTypography
} from "@manual_encounters/encounter_update_status/pings/components/InsurancePlansHookForm.styles";
import {InsurancePlan} from "@/api/dto/client-profile";
import {GenericObject} from "@/hooks/useForm";

export type InsurancePlanRow = {
  insuranceProvider: InsurancePlan;
  insuranceValue: string;
  otherTranslatedName?: string;
}

type Props = {
  form: GenericObject;
  handleChange: (e: React.ChangeEvent<HTMLInputElement> | null, name: string, value: any) => void;
  defaultSelectedPlan: InsurancePlan | { id: string, value?: string };
  disabled?: boolean;
  isEditing?: boolean;
}

const InsurancePlanFormNew = ({ form, handleChange, defaultSelectedPlan, disabled = false, isEditing }: Props) => {
  const [insurers, setInsurers] = useState<InsurancePlan[]>([]);
  const [touchedFields, setTouchedFields] = useState<Record<number, Record<string, boolean>>>({});
  const [selectedRow, setSelectedRow] = useState(defaultSelectedPlan);
  const [showUpdateDisclaimer, setShowUpdateDisclaimer] = useState(false);
  const { carecoApi } = useContext(CarecoApiContext);

  const items = () => {
    const inUse: Record<string, boolean> = {};
    form.insurancePlans.value.forEach((plan: InsurancePlanRow) => {
      inUse[plan.insuranceProvider.insuranceCompanyName] = true;
    });
    return insurers.filter((insurer) => !inUse[insurer.insuranceCompanyName]);
  };

  useLayoutEffect(() => {
    carecoApi?.readInsurers().then((insurances) => {
      setInsurers(insurances);
    });
  }, []);

  //calling handleChange instead of storing this value in state was having issues with updates on the uncontrolled components
  //this is a work around to force updates
  useEffect(() => {
    handleChange(null, 'selectedInsurancePlan', selectedRow);
  }, [selectedRow]);

  useEffect(() => {
    if (form.insurancePlans.value.length === 0 && defaultSelectedPlan) {
      handleChange(null, 'insurancePlans', [{
        insuranceProvider: defaultSelectedPlan,
        insuranceValue: defaultSelectedPlan.value,
      }]);
    }
  }, [defaultSelectedPlan]);

  const validateExistingRows = () => {
    if (isEditing) {
      setShowUpdateDisclaimer(true);
    }
  };

  const handleSelectRow = (insuranceProvider: InsurancePlan) => {
    validateExistingRows();
    setSelectedRow(insuranceProvider);
  };

  const handleAddRow = () => {
    validateExistingRows();
    const id = `NEW${Math.floor(Math.random() * 1000)}`;
    handleChange(null, 'insurancePlans', [
      ...form.insurancePlans.value,
      {
        insuranceProvider: { id },
        insuranceValue: '',
      },
    ]);
  };

  const handleDropdownChange = (index: number, newValue: InsurancePlan, previousValue: InsurancePlan) => {
    handleChange(null, `insurancePlans`,
      form.insurancePlans.value.map((insurancePlan: InsurancePlanRow, i: number) => {
        if (i === index) {
          return { ...insurancePlan, insuranceProvider: formatCompanySelection(newValue, previousValue) };
        }
        return insurancePlan;
      }));
    setTouchedFields((prev) => ({
      [index]: {
        ...prev[index],
        insuranceProvider: true
      },
    }));
    if (isEditing) {
      setShowUpdateDisclaimer(true);
    }
  };

  const handleInputChange = (index: number, fieldName: string, value: string) => {
    handleChange(null, `insurancePlans`,
      form.insurancePlans.value.map((insurancePlan: InsurancePlanRow, i: number) => {
        if (i === index) {
          return { ...insurancePlan, [fieldName]: value };
        }
        return insurancePlan;
      }));
    setTouchedFields((prev) => ({
      [index]: {
        ...prev[index],
        insuranceValue: true
      },
    }));
    if (isEditing) {
      setShowUpdateDisclaimer(true);
    }
  };

  const handleRemoveRow = (index: number, isSelected: boolean) => {
    handleChange(null, 'insurancePlans', form.insurancePlans.value.filter((_: InsurancePlanRow, i: number) => i !== index));
    if (isSelected) {
      const nextIndex = index ? 0 : 1;
      setSelectedRow(form.insurancePlans.value[nextIndex]?.insuranceProvider);
    }
    if (isEditing) {
      setShowUpdateDisclaimer(true);
    }
  };

  const formatCompanySelection = ({ code, insurerId, insuranceCompanyName }: InsurancePlan, previousValue: InsurancePlan) => {
    return { ...previousValue, code, insurerId, insuranceCompanyName };
  };

  const renderInsurances = () => {
    return form.insurancePlans.value.map((plan: InsurancePlanRow, index: number) => {
      const hasProviderError = touchedFields[index]?.insuranceProvider
        && !plan.insuranceProvider.code && plan.insuranceProvider.insuranceCompanyName !== 'No Insurance';
      const hasIdError = touchedFields[index]?.insuranceValue && !plan.insuranceValue;
      const hasOtherTranslatedNameError = touchedFields[index]?.otherTranslatedName && !plan.otherTranslatedName;
      const isSelected = plan.insuranceProvider.id === selectedRow?.id;
      const showPrimaryLabel = isSelected && !hasProviderError && !hasOtherTranslatedNameError;
      const showOtherInsuranceInput = plan.insuranceProvider.code === 'OTHER';
      return (
        <StyledRow key={plan.insuranceProvider.id}>
          <div>
            <StyledControlsContainer className="radioDropdown">
              <StyledRadioButton
                name={`insurance${index}`}
                id={`insurance${index}`}
                label=""
                value={plan.insuranceProvider.id}
                onChange={() => handleSelectRow(plan.insuranceProvider)}
                checked={isSelected}
                disabled={disabled}
              />
              <StyledDropdownContainer>
                <StyledIdDropdownList
                  handleChange={(newValue: InsurancePlan) => handleDropdownChange(index, newValue, plan.insuranceProvider)}
                  value={insurers.find((insurer) => insurer.insuranceCompanyName === plan.insuranceProvider.insuranceCompanyName)}
                  id={`insuranceProvider${index}`}
                  items={items()}
                  isWithinModal
                  error={hasProviderError}
                  errorMessage="Required Field"
                  disabled={disabled}
                />
                {showOtherInsuranceInput && (
                  <StyledOtherNameInput
                    name={`insurancePlans[${index}].otherTranslatedName`}
                    defaultValue={plan.otherTranslatedName || ''}
                    id={`otherTranslatedName${index}`}
                    error={hasOtherTranslatedNameError}
                    errorMessage="Required Field"
                    isDisabled={disabled}
                    onChange={(e) => handleInputChange(index, 'otherTranslatedName', e.target.value)}
                    labelText="Other Name"
                    required="required"
                  />
                )}

                {showPrimaryLabel && <StyledSmallTypography>Primary Insurance</StyledSmallTypography>}
              </StyledDropdownContainer>
            </StyledControlsContainer>
          </div>
          <Spacer itemWidth={sizes.small} />
          <StyledControlsContainer className="inputContainer">
            {!(plan.insuranceProvider.insuranceCompanyName === 'No Insurance' ||
              plan.insuranceProvider.insuranceCompanyName === 'Self Pay')  && (
              <StyledIdInput
                name={`insurancePlans[${index}].insuranceValue`}
                defaultValue={plan.insuranceValue}
                id={`insuranceValue${index}`}
                data-testid={`insuranceValue${index}`}
                error={hasIdError}
                errorMessage="Required Field"
                isDisabled={disabled}
                onChange={(e) => handleInputChange(index, 'insuranceValue', e.target.value)}
              />
            )}
            {form.insurancePlans.value.length > 1 && !disabled && (
              <StyledIconButton onClick={() => handleRemoveRow(index, isSelected)}>
                <StyledIconClose
                  iconClass="times"
                  color={colors.purple4}
                />
              </StyledIconButton>
            )}
          </StyledControlsContainer>
        </StyledRow>
      );
    });
  };

  return (
    <Fragment>
      <StyledRow>
        <StyledLabelFormFieldTypography className="requiredField">Insurance Provider</StyledLabelFormFieldTypography>
        <StyledLabelFormFieldTypography className="requiredField">Insurance Plan ID</StyledLabelFormFieldTypography>
      </StyledRow>
      {renderInsurances()}
      {!disabled && (
        <StyledButton styleType={StyleType.TERTIARY} onClick={handleAddRow}>
          <StyledIconWrapper>
            <Icon color={colors.purple4} iconClass="plus" />
          </StyledIconWrapper>
          Add Another Insurance
        </StyledButton>
      )}
      {showUpdateDisclaimer && (
        <StyledDisclaimerText>
          You are about to update insurance information for this event only. If you are trying to make a payer change
          for this client, please select the "Update Client Status" option on the client event card.
        </StyledDisclaimerText>
      )}
    </Fragment>
  );
};

export default InsurancePlanFormNew;
